import { useState } from 'react';
import * as api from '../../api/endpoints';
import { useProjectStore } from '../../store/useProjectStore';
import { useExtractionStore } from '../../store/useExtractionStore';
import { useCollectionStore } from '../../store/useCollectionStore';
import type { ExtractionPageMeta, ExtractedComponent } from '../../types';

const COMMON_CATEGORIES = [
  'Leitungsschutzschalter',
  'FI/LS-Schalter',
  'Fehlerstromschutzschalter',
  'Brandschutzschalter',
  'Leistungsschalter',
  'Schütz',
  'Motorschutzschalter',
  'Überspannungsschutz',
  'Lasttrennschalter',
  'Sicherungslasttrennschalter',
  'NH-Sicherung',
  'Hutschienen-Netzteil',
  'Klemme',
  'Zeitrelais',
  'Stromstoßschalter',
  'Dimmer',
  'Treppenlichtautomat',
  'Energiezähler',
  'Steckdose',
  'Schalter',
];

interface AddComponentModalProps {
  mode: 'add' | 'duplicate';
  page: ExtractionPageMeta;
  sourceComponent?: ExtractedComponent;
  onClose: () => void;
  onSaved: () => void;
}

export function AddComponentModal({ mode, page, sourceComponent, onClose, onSaved }: AddComponentModalProps) {
  const [id, setId] = useState(mode === 'duplicate' && sourceComponent ? `${sourceComponent.id}_copy` : '');
  const [category, setCategory] = useState(sourceComponent?.category || '');
  const [spec, setSpec] = useState(sourceComponent?.spec_grouping_string || '');
  const [quantity, setQuantity] = useState(sourceComponent?.quantity || 1);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!id.trim() || !category.trim()) return;
    setSaving(true);
    try {
      await api.addComponent(page.zoneId, page.file, page.page, {
        id: id.trim(),
        category: category.trim(),
        spec_grouping_string: spec.trim() || undefined,
        quantity,
      });
      // Refresh both stores
      const ext = await api.getExtraction();
      if (ext) {
        useProjectStore.getState().setExtraction(ext);
        const key = `${page.file}:${page.page}`;
        for (const zone of ext.zones) {
          for (const pg of zone.pages) {
            if (pg.file === page.file && pg.page === page.page) {
              useExtractionStore.getState().setPageResult(key, pg);
              break;
            }
          }
        }
      }
      useCollectionStore.getState().touchDiagram(page.file);
      onSaved();
      onClose();
    } catch (e) {
      useProjectStore.getState().setToast(`${mode === 'duplicate' ? 'Duplicate' : 'Add'} failed: ${e}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <h2>{mode === 'duplicate' ? 'Duplicate Component' : 'Add Component'}</h2>
        <div className="modal-form">
          <div className="modal-field">
            <label>ID *</label>
            <input
              autoFocus
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="e.g. Q1, F3"
            />
          </div>
          <div className="modal-field">
            <label>Category *</label>
            <input
              list="component-categories"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Leitungsschutzschalter"
            />
            <datalist id="component-categories">
              {COMMON_CATEGORIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div className="modal-field">
            <label>Spec</label>
            <input
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
              placeholder="e.g. B16 3P"
            />
          </div>
          <div className="modal-field">
            <label>Quantity</label>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-approve" onClick={handleSave} disabled={saving || !id.trim() || !category.trim()}>
            {saving ? 'Saving...' : mode === 'duplicate' ? 'Duplicate' : 'Add'}
          </button>
          <button className="btn-reject" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
